export type BlockType =
  | 'paragraph'
  | 'heading'
  | 'image'
  | 'gallery'
  | 'video'
  | 'embed'
  | 'code'
  | 'quote'
  | 'columns'
  | 'button'
  | 'html'
  | 'postgrid'
  | 'spacer'
  | 'menu'
  | 'div'
  | 'template'
  | 'newsletter_signup'
  | 'post_card'
  | 'art_generator'
  | 'post_search';

/** Escala de espaçamento (margem/padding) — mesmos valores físicos usados pelo padding do Div (ver SPACING_PX em blockSize.ts). */
export type SpacingScale = 'none' | 'sm' | 'md' | 'lg';

export interface BaseBlock {
  id: string;
  type: BlockType;
  /** Nome interno exibido apenas no editor para facilitar a identificação visual do bloco. */
  internalName?: string;
  /** Mantém o bloco fixo na tela pública e aderente ao topo no editor. */
  sticky?: boolean;
  /** Largura em px — 0, null ou ausente significa automática. */
  width?: number | null;
  /** Unidade da largura. */
  widthUnit?: 'px' | '%';
  /** Altura em px — 0, null ou ausente significa automática. */
  height?: number | null;
  /** Unidade da altura. */
  heightUnit?: 'px' | '%';
  /** Espaçamento externo — distância entre este bloco e os vizinhos. Ausente/'none' = sem margem extra. */
  margin?: SpacingScale;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  /**
   * Espaçamento interno — distância entre a borda do bloco e seu conteúdo.
   * Em DivBlock este campo já existe com efeito próprio (classe Tailwind no
   * próprio elemento, ver getDivClassName em divStyle.ts) — para os demais
   * tipos é aplicado no wrapper genérico (ver getBlockSizeStyle).
   */
  padding?: SpacingScale;
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
}

/** 'inherit' deixa a espessura vir do padrão do bloco/tema (ex.: título já é bold por padrão). */
export type TextWeight = 'inherit' | 'normal' | 'medium' | 'semibold' | 'bold';

/** Sobrescritas de estilo de texto — cor/tamanho vazios ou 0 significam "herdar" do tema/aparência. */
export interface TextStyleFields {
  color: string;
  fontSize: number;
  fontWeight: TextWeight;
}

export interface ParagraphBlock extends BaseBlock, TextStyleFields {
  type: 'paragraph';
  content: string;
}

export interface HeadingBlock extends BaseBlock, TextStyleFields {
  type: 'heading';
  level: 1 | 2 | 3 | 4 | 5 | 6;
  content: string;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt: string;
  caption: string;
  width: number | null;
  height: number | null;
  /** Recorte: auto (proporção natural), quadrado ou círculo. */
  shape: 'auto' | 'square' | 'circle';
  /** Arredondamento dos cantos — ignorado quando shape é 'circle' (sempre totalmente redondo). */
  radius: 'none' | 'sm' | 'lg' | 'full';
  /** Link opcional ao clicar na imagem. */
  linkUrl?: string;
  /** Destino do link opcional. */
  linkTarget?: '_self' | '_blank';
  /** Abre o link em um modal, em vez de navegar para outra página. */
  linkModal?: boolean;
  /** Clique para ampliar em uma janela (lightbox) no site público. */
  lightbox: boolean;
}

export interface GalleryBlock extends BaseBlock {
  type: 'gallery';
  images: Array<{ src: string; alt: string; caption?: string }>;
  columns: 2 | 3 | 4;
  layout?: 'grid' | 'masonry';
  radius?: 'none' | 'sm' | 'lg' | 'full';
  lightbox?: boolean;
  loadingMode?: 'all' | 'progressive';
  initialItems?: number;
  itemsPerLoad?: number;
}

export interface VideoBlock extends BaseBlock {
  type: 'video';
  url: string;
  provider: 'youtube' | 'vimeo' | 'file';
  /** Imagem exibida antes da reprodução, opcional. */
  posterUrl?: string;
  /** Proporção visual do player. */
  aspectRatio?: 'horizontal' | 'vertical' | 'square';
}

export interface EmbedBlock extends BaseBlock {
  type: 'embed';
  url: string;
  caption: string;
}

export interface CodeBlock extends BaseBlock {
  type: 'code';
  language: string;
  code: string;
}

export interface QuoteBlock extends BaseBlock {
  type: 'quote';
  content: string;
  author: string;
}

export interface ColumnsBlock extends BaseBlock {
  type: 'columns';
  columns: Block[][];
  layout: '50-50' | '33-33-33' | '66-33' | '33-66';
}

export interface ButtonBlock extends BaseBlock, TextStyleFields {
  type: 'button';
  label: string;
  url: string;
  variant: 'primary' | 'secondary' | 'outline';
  align: 'left' | 'center' | 'right';
}

export interface HtmlBlock extends BaseBlock {
  type: 'html';
  html: string;
}

export interface PostGridBlock extends BaseBlock {
  type: 'postgrid';
  count: number;
  categoryId: string | null;
  categoryName: string | null;
  columns: 2 | 3 | 4;
  style: 'card' | 'overlay' | 'list';
  /** Opcionais para compatibilidade com grids salvos antes destes controles. */
  imageFormat?: 'wide' | 'square' | 'portrait';
  imageRadius?: 'none' | 'sm' | 'lg' | 'full';
  blockRadius?: 'none' | 'sm' | 'lg' | 'full';
  titleColor?: string;
  textColor?: string;
  backgroundColor?: string;
  showImage: boolean;
  showExcerpt: boolean;
  showDate: boolean;
}

export interface SpacerBlock extends BaseBlock {
  type: 'spacer';
  /** Altura em pixels. */
  height: number;
}

export interface MenuBlock extends BaseBlock, TextStyleFields {
  type: 'menu';
  style: 'horizontal' | 'vertical';
  /** Alinhamento — só se aplica ao estilo horizontal. */
  align: 'left' | 'center' | 'right';
  /** Arredondamento do dropdown/submenu. Ausente = 'sm' para menus antigos. */
  dropdownRadius?: 'none' | 'sm' | 'md' | 'lg';
  /**
   * Comportamento abaixo de `md`. Opcional para compatibilidade com menus
   * salvos antes deste controle (ausente = 'inline', o comportamento antigo).
   * Só se aplica ao estilo horizontal.
   */
  mobile?: 'inline' | 'hamburger';
  /** Cor exclusiva do botão hambúrguer no mobile. Vazio = usar a cor do texto. */
  hamburgerColor?: string;
  /** Fundo opcional do menu; vazio = transparente. */
  backgroundColor?: string;
  /** Opacidade do fundo em percentual. */
  backgroundOpacity?: number;
  /** Fundo do dropdown/submenu; vazio = branco padrão. */
  dropdownBackgroundColor?: string;
  /** Fundo dos itens do dropdown/submenu ao passar o cursor. */
  dropdownHoverBackgroundColor?: string;
  /** Cor dos textos do dropdown/submenu; vazio = usar a cor do menu. */
  dropdownTextColor?: string;
}

/** Hambúrguer só faz sentido no menu horizontal; ausente = comportamento antigo. */
export function usesHamburger(block: MenuBlock): boolean {
  return block.style === 'horizontal' && block.mobile === 'hamburger';
}

/**
 * Container flexível — pode conter qualquer outro bloco, inclusive outras
 * divs (aninhamento livre, sem limite de profundidade), para montar layouts
 * customizados que os blocos prontos não cobrem.
 */
export interface DivBlock extends BaseBlock {
  type: 'div';
  children: Block[];
  /** Largura do conteúdo — largura total ou contida (max-width centralizado). */
  contentWidth: 'boxed' | 'full';
  direction: 'row' | 'column';
  gap: 'none' | 'sm' | 'md' | 'lg';
  /** Altura mínima em px — 0 significa automática. */
  minHeight: number;
  justify: 'start' | 'center' | 'end' | 'between';
  align: 'start' | 'center' | 'end' | 'stretch';
  /** Só se aplica quando direction é 'row'. */
  wrap: boolean;
  /** Cor de fundo — string vazia é transparente. */
  bgColor: string;
  bgImageUrl: string;
  padding: 'none' | 'sm' | 'md' | 'lg';
  radius: 'none' | 'sm' | 'lg' | 'full';
}

export interface TemplateBlock extends BaseBlock {
  type: 'template';
  /** Modelo de aparência referenciado. Modelos dinâmicos renderizam sempre o conteúdo atual deste id. */
  templateId: string;
  templateName: string;
}

/**
 * Formulário de inscrição em newsletter — email + Turnstile no site público,
 * chama a Edge Function `newsletter-subscribe` (double opt-in, ver módulo
 * de newsletter). No admin renderiza como preview estático.
 */
export interface NewsletterSignupBlock extends BaseBlock {
  type: 'newsletter_signup';
  title: string;
  description: string;
  buttonLabel: string;
  successMessage: string;
}

/** Um post publicado específico, escolhido pelo admin — imagem + título + resumo + link. Diferente do PostGridBlock (grid automático por categoria): aqui é uma referência direta a um post. */
export interface PostCardBlock extends BaseBlock {
  type: 'post_card';
  postId: string | null;
  /** Denormalizado — evita esperar uma consulta pra mostrar algo no painel de configurações (mesmo padrão de categoryId/categoryName em PostGridBlock). */
  postTitle: string | null;
}

/** Gerador público de arte: a foto do visitante é processada somente em memória. */
export interface ArtGeneratorBlock extends BaseBlock {
  type: 'art_generator';
  /** Vazio exibe todos os modelos publicados deste site. */
  templateIds: string[];
  title: string;
  description: string;
}

/** Busca pública de posts publicados do próprio site. */
export interface PostSearchBlock extends BaseBlock {
  type: 'post_search';
  title: string;
  description: string;
  placeholder: string;
  buttonLabel: string;
  emptyMessage: string;
  resultsLimit: 3 | 6 | 9;
  /** Vazio = herdar a fonte do tema. */
  fontFamily?: string;
  /** Vazio = herdar a cor de texto do tema. */
  textColor?: string;
  /** Vazio = herdar o fundo dos boxes do tema. */
  backgroundColor?: string;
  /** Zero ou ausente = herdar o tamanho de texto do tema. */
  fontSize?: number;
  /** Cor do botão; vazio = cor primária do tema. */
  buttonColor?: string;
  /** Cor do texto do botão; vazio = branco. */
  buttonTextColor?: string;
  /** Fundo do campo; vazio = fundo padrão do tema. */
  inputBackgroundColor?: string;
  /** Opacidade do fundo do campo em percentual. */
  inputBackgroundOpacity?: number;
}

export type Block =
  | ParagraphBlock
  | HeadingBlock
  | ImageBlock
  | GalleryBlock
  | VideoBlock
  | EmbedBlock
  | CodeBlock
  | QuoteBlock
  | ColumnsBlock
  | ButtonBlock
  | HtmlBlock
  | PostGridBlock
  | SpacerBlock
  | MenuBlock
  | DivBlock
  | TemplateBlock
  | NewsletterSignupBlock
  | PostCardBlock
  | ArtGeneratorBlock
  | PostSearchBlock;

const DEFAULT_TEXT_STYLE: TextStyleFields = { color: '', fontSize: 0, fontWeight: 'inherit' };

export function createBlock(type: BlockType): Block {
  const id = crypto.randomUUID();
  switch (type) {
    case 'paragraph':
      return { id, type: 'paragraph', content: '', ...DEFAULT_TEXT_STYLE };
    case 'heading':
      return { id, type: 'heading', level: 2, content: '', ...DEFAULT_TEXT_STYLE };
    case 'image':
      return { id, type: 'image', src: '', alt: '', caption: '', width: null, height: null, shape: 'auto', radius: 'none', linkUrl: '', linkTarget: '_self', linkModal: false, lightbox: false };
    case 'gallery':
      return { id, type: 'gallery', images: [], columns: 3, layout: 'grid', radius: 'none', lightbox: true, loadingMode: 'progressive', initialItems: 12, itemsPerLoad: 12 };
    case 'video':
      return { id, type: 'video', url: '', provider: 'youtube', posterUrl: '', aspectRatio: 'horizontal' };
    case 'embed':
      return { id, type: 'embed', url: '', caption: '' };
    case 'code':
      return { id, type: 'code', language: 'typescript', code: '' };
    case 'quote':
      return { id, type: 'quote', content: '', author: '' };
    case 'columns':
      return { id, type: 'columns', columns: [[], []], layout: '50-50' };
    case 'button':
      return { id, type: 'button', label: 'Clique aqui', url: '#', variant: 'primary', align: 'left', ...DEFAULT_TEXT_STYLE };
    case 'html':
      return { id, type: 'html', html: '' };
    case 'postgrid':
      return {
        id,
        type: 'postgrid',
        count: 3,
        categoryId: null,
        categoryName: null,
        columns: 3,
        style: 'card',
        imageFormat: 'wide',
        imageRadius: 'none',
        blockRadius: 'none',
        titleColor: '',
        textColor: '',
        backgroundColor: '',
        showImage: true,
        showExcerpt: true,
        showDate: true,
      };
    case 'spacer':
      return { id, type: 'spacer', height: 40 };
    case 'menu':
      return {
        id,
        type: 'menu',
        style: 'horizontal',
        align: 'center',
        dropdownRadius: 'none',
        mobile: 'inline',
        hamburgerColor: '',
        backgroundColor: '',
        backgroundOpacity: 100,
        dropdownBackgroundColor: '',
        dropdownHoverBackgroundColor: '',
        dropdownTextColor: '',
        ...DEFAULT_TEXT_STYLE,
      };
    case 'div':
      return {
        id,
        type: 'div',
        children: [],
        contentWidth: 'full',
        direction: 'column',
        gap: 'md',
        width: 0,
        height: 0,
        minHeight: 0,
        justify: 'start',
        align: 'stretch',
        wrap: false,
        bgColor: '',
        bgImageUrl: '',
        padding: 'md',
        radius: 'none',
      };
    case 'template':
      return { id, type: 'template', templateId: '', templateName: '', internalName: 'Modelo pronto' };
    case 'newsletter_signup':
      return {
        id,
        type: 'newsletter_signup',
        title: 'Assine nossa newsletter',
        description: 'Receba os posts mais recentes por email.',
        buttonLabel: 'Inscrever-se',
        successMessage: 'Quase lá! Confira seu email para confirmar a inscrição.',
      };
    case 'post_card':
      return { id, type: 'post_card', postId: null, postTitle: null };
    case 'art_generator':
      return {
        id,
        type: 'art_generator',
        templateIds: [],
        title: 'Crie sua arte para compartilhar',
        description: 'Envie sua foto, escolha uma arte e baixe o resultado pronto para as redes sociais.',
      };
    case 'post_search':
      return {
        id,
        type: 'post_search',
        title: 'Encontre o que procura',
        description: 'Busque conteúdos publicados neste site.',
        placeholder: 'Digite o que você procura',
        buttonLabel: 'Buscar',
        emptyMessage: 'Nenhum post encontrado para esta busca.',
        resultsLimit: 6,
        fontFamily: '',
        textColor: '',
        backgroundColor: '',
        fontSize: 0,
        buttonColor: '',
        buttonTextColor: '',
        inputBackgroundColor: '',
        inputBackgroundOpacity: 100,
      };
    default:
      return { id, type: 'paragraph', content: '', ...DEFAULT_TEXT_STYLE };
  }
}
